/**
 * Security Interceptor for API Client
 *
 * Provides automatic security event tracking for API requests and responses.
 * Tracks authentication failures, rate limiting, and suspicious activity.
 */

import { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { securityEventTracker } from '../services/securityEventTracker';
import { securityLogger } from './loggerInstance';
import { SecurityEventType } from '../services/securityEventTracker';

declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: RequestMetadata;
  }
  export interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}

// Security-sensitive endpoints that should be monitored
const SECURITY_ENDPOINTS = [
  '/api/v1/auth/',
  '/api/v1/security/',
  '/api/v1/users/profile',
  '/api/v1/users/password',
];

// Request metadata interface
interface RequestMetadata {
  startTime: number;
  endpoint: string;
  method: string;
  isSecurityEndpoint: boolean;
}

/**
 * Check if an endpoint is security-sensitive
 */
function isSecurityEndpoint(url: string): boolean {
  return SECURITY_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Get sanitized endpoint for logging (remove sensitive data)
 */
function getSanitizedEndpoint(url: string): string {
  // Remove query parameters that might contain sensitive data
  const urlObj = new URL(url, window.location.origin);
  return urlObj.pathname;
}

/**
 * Extract error details for security logging
 */
function extractErrorDetails(error: AxiosError): {
  status: number;
  message: string;
  isSecurityError: boolean;
  errorType: string;
} {
  const status = error.response?.status || 0;
  const responseData = error.response?.data as { message?: string } | undefined;
  const message = responseData?.message || error.message || 'Unknown error';
  const isSecurityError = status === 401 || status === 403 || status === 429;
  const errorType = error.code || 'HTTP_ERROR';

  return { status, message, isSecurityError, errorType };
}

/**
 * Setup security interceptors for an Axios instance
 */
export function setupSecurityInterceptors(apiClient: AxiosInstance): void {
  // Request interceptor - track outgoing requests
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const startTime = performance.now();
      const endpoint = getSanitizedEndpoint(config.url || '');
      const method = (config.method || 'GET').toUpperCase();
      const isSecurityEndpoint_ = isSecurityEndpoint(config.url || '');

      // Store metadata for response interceptor
      config.metadata = {
        startTime,
        endpoint,
        method,
        isSecurityEndpoint: isSecurityEndpoint_,
      } as RequestMetadata;

      // Log security-sensitive requests
      if (isSecurityEndpoint_) {
        securityLogger.debug('Security API request', {
          method,
          endpoint,
          timestamp: new Date().toISOString(),
        });
      }

      return config;
    },
    (error) => {
      securityLogger.error('Request interceptor error', { error });
      return Promise.reject(error);
    },
  );

  // Response interceptor - track responses and errors
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      const metadata = response.config.metadata;

      if (metadata) {
        const duration = performance.now() - metadata.startTime;

        // Track successful security operations
        if (metadata.isSecurityEndpoint) {
          securityEventTracker.trackEvent({
            type: 'security_settings_access',
            severity: 'low',
            success: true,
            metadata: {
              endpoint: metadata.endpoint,
              method: metadata.method,
              status: response.status,
              duration,
            },
            performanceMetrics: {
              duration,
              timestamp: performance.now(),
            },
          });

          securityLogger.debug('Security API response', {
            method: metadata.method,
            endpoint: metadata.endpoint,
            status: response.status,
            duration: `${duration.toFixed(2)}ms`,
          });
        }

        // Track slow responses as potential performance issues
        if (duration > 5000) {
          securityEventTracker.trackEvent({
            type: 'performance_issue',
            severity: 'medium',
            success: true,
            errorMessage: `Slow API response: ${duration.toFixed(2)}ms`,
            metadata: {
              endpoint: metadata.endpoint,
              method: metadata.method,
              status: response.status,
              duration,
            },
            performanceMetrics: {
              duration,
              timestamp: performance.now(),
            },
          });
        }
      }

      return response;
    },
    (error: AxiosError) => {
      const metadata = error.config?.metadata;
      const errorDetails = extractErrorDetails(error);

      if (metadata) {
        const duration = performance.now() - metadata.startTime;

        // Track API errors with security implications
        securityEventTracker.trackAPIError(error, metadata.endpoint, metadata.method, {
          duration,
          isSecurityEndpoint: metadata.isSecurityEndpoint,
        });

        // Log security-related errors with more detail
        if (metadata.isSecurityEndpoint || errorDetails.isSecurityError) {
          securityLogger.warn('Security API error', {
            method: metadata.method,
            endpoint: metadata.endpoint,
            status: errorDetails.status,
            message: errorDetails.message,
            errorType: errorDetails.errorType,
            duration: `${duration.toFixed(2)}ms`,
            isSecurityError: errorDetails.isSecurityError,
          });

          // Track specific security events
          if (errorDetails.status === 401) {
            securityEventTracker.trackEvent({
              type: 'suspicious_activity',
              severity: 'medium',
              success: false,
              errorMessage: 'Unauthorized access attempt',
              metadata: {
                endpoint: metadata.endpoint,
                method: metadata.method,
                status: errorDetails.status,
                possibleTokenTheft: true,
              },
            });
          } else if (errorDetails.status === 429) {
            securityEventTracker.trackEvent({
              type: 'rate_limit_exceeded',
              severity: 'medium',
              success: false,
              errorMessage: 'Rate limit exceeded',
              metadata: {
                endpoint: metadata.endpoint,
                method: metadata.method,
                status: errorDetails.status,
                retryAfter: error.response?.headers['retry-after'],
              },
            });
          }
        }
      } else {
        // Handle errors without metadata (shouldn't happen normally)
        securityLogger.error('API error without metadata', {
          status: errorDetails.status,
          message: errorDetails.message,
          url: error.config?.url,
        });
      }

      return Promise.reject(error);
    },
  );

  securityLogger.info('Security interceptors configured for API client');
}

/**
 * Track manual security events (for use outside of API calls)
 */
export function trackSecurityEvent(
  eventType: string,
  success: boolean,
  details?: {
    errorMessage?: string;
    metadata?: Record<string, any>;
    severity?: 'low' | 'medium' | 'high' | 'critical';
  },
): void {
  securityEventTracker.trackEvent({
    type: eventType as SecurityEventType,
    severity: details?.severity || (success ? 'low' : 'medium'),
    success,
    errorMessage: details?.errorMessage,
    metadata: details?.metadata,
  });

  securityLogger.info('Manual security event tracked', {
    eventType,
    success,
    severity: details?.severity || (success ? 'low' : 'medium'),
  });
}

/**
 * Track user session events
 */
export function trackSessionEvent(
  eventType: 'session_start' | 'session_end' | 'session_timeout' | 'session_expired',
  sessionId?: string,
  metadata?: Record<string, any>,
): void {
  securityEventTracker.trackEvent({
    type: eventType === 'session_expired' ? 'session_expired' : 'security_settings_access',
    severity: eventType === 'session_expired' ? 'medium' : 'low',
    success: true,
    sessionId,
    metadata: {
      ...metadata,
      sessionEventType: eventType,
    },
  });

  securityLogger.info('Session event tracked', {
    eventType,
    sessionId,
    timestamp: new Date().toISOString(),
  });
}

export default {
  setupSecurityInterceptors,
  trackSecurityEvent,
  trackSessionEvent,
};
